import { useMutation, useQuery } from '@tanstack/react-query';
import APIService from '../APIService';

const useUser = () => {
  return useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const user = await APIService.GET('/user');
      return user.data;
    },
    // AUTH-1: role lives on the ['user'] payload. A role change made by an admin
    // invalidates ['user'] only on the admin's client; the promoted user's own
    // session must refetch independently. Explicit refetchOnWindowFocus guards
    // this convergence path against a future global QueryClient default flip.
    refetchOnWindowFocus: true,
  });
};

const useUpdateUser = () => {
  const { data: user } = useUser();
  return useMutation({
    mutationFn: async (updates: any) => {
      const updatedUser = await APIService.PUT(`/user/${user._id}`, undefined, updates);
      return updatedUser.data;
    },
  });
};

const useOrgMember = ({ orgPath }: { orgPath: string }) => {
  const { data: user } = useUser();
  return user?.orgs?.find((org: any) => org.url === orgPath)?.members?.[user?._id];
};

const useConfirmUser = () => {
  return useMutation({
    mutationFn: async ({ code, userName, clientId }: { code: string; userName: string; clientId: string }) => {
      return APIService.POST('/auth/verify', undefined, {
        code,
        userName,
        clientId,
      });
    },
  });
};

const useFinalizeAccount = () => {
  return useMutation({
    mutationFn: async ({
      userName,
      userPoolId,
      firstName,
      lastName,
    }: {
      userName: string;
      userPoolId: string;
      firstName: string;
      lastName: string;
    }) => {
      return APIService.POST('/auth/finalizeAccount', undefined, {
        userName,
        userPoolId,
        firstName,
        lastName,
      });
    },
  });
};

const useCheckUserIsAdmin = () => {
  const { data: user } = useUser();
  return !!user?.admin;
};

export { useOrgMember, useUpdateUser, useConfirmUser, useFinalizeAccount, useCheckUserIsAdmin };

export default useUser;
